"use client";

import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";
import type { FooterDathaconsProps } from "src/lib/utils/interfaces/components/footer-dathacons";
import { toAssetImages } from "src/lib/utils";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

const ICONS: Record<string, typeof FacebookIcon> = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  youtube: YouTubeIcon,
  linkedin: LinkedInIcon,
  twitter: FacebookIcon,
  tiktok: FacebookIcon,
};

function sectionIdFromTarget(target: string) {
  return target.replace(/^#/, "");
}

const FooterDathacons: React.FC<FooterDathaconsProps> = (props) => {
  const {
    logoUrl,
    companyName,
    companyAddress,
    openingHours,
    categoryTitle = "Danh mục",
    categoryLinks,
    contactTitle = "Liên hệ",
    hotline,
    email,
    socialItems,
    connectLabel = "Kết nối với chúng tôi",
    copyright,
    policyLabel = "Chính sách và điều kiện",
  } = props;

  const pathname = usePathname();
  const router = useRouter();
  const [logoFailed, setLogoFailed] = useState(false);

  const go = (action: "scroll" | "link", target: string) => {
    if (action === "link") {
      if (target.startsWith("http")) window.open(target, "_blank", "noopener,noreferrer");
      else router.push(target);
      return;
    }
    const id = sectionIdFromTarget(target);
    if (pathname !== "/") {
      router.push(`/?scrollTo=${id}`);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer
      className="bg-[#0b1d32] content-stretch flex flex-col items-center justify-center relative shrink-0 w-full text-white"
      data-name="footer Footer"
    >
      <div className="relative flex w-full max-w-[1600px] shrink-0 flex-col items-center justify-center px-4 py-5 sm:px-5 md:min-h-[110px] md:py-0">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 border border-[rgba(255,255,255,0.1)] border-solid"
        />
        <div className="relative flex w-full max-w-[1120px] flex-col gap-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div className="relative min-w-0 max-w-full shrink" data-name="Logo container">
            {!logoFailed ? (
              <img
                src={logoUrl ? toAssetImages(logoUrl) : undefined}
                alt={companyName}
                width={200}
                height={44}
                decoding="async"
                onError={() => setLogoFailed(true)}
                className="h-9 w-auto max-h-10 max-w-[min(200px,100%)] object-contain object-left opacity-95 sm:h-10"
              />
            ) : (
              <p className="line-clamp-2 max-w-full text-[15px] font-semibold leading-snug text-white/95 sm:text-base">
                {companyName}
              </p>
            )}
          </div>
          <div
            className="flex w-full min-w-0 flex-col gap-3 text-white sm:w-auto sm:flex-row sm:items-center sm:justify-end sm:gap-5"
            data-name="Social links row"
          >
            <div className="text-[15px] font-medium leading-snug sm:text-[17px]">
              <p>{connectLabel}</p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              {socialItems.map((s, i) => {
                const Icon = (s.type && ICONS[s.type]) || FacebookIcon;
                if (!s.url) return null;
                return (
                  <a
                    key={`${s.type}-${i}`}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 text-[#d0d1d0] transition-colors hover:text-[#ffa411]"
                    aria-label={s.type ?? "social"}
                  >
                    <Icon sx={{ fontSize: 26 }} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="relative flex w-full max-w-[1600px] shrink-0 flex-col items-center justify-center px-4 py-8 sm:px-5">
        <div className="flex w-full max-w-[1120px] flex-col items-start justify-between gap-10 sm:flex-row sm:gap-10">
          <div className="min-w-0 max-w-full flex-1" data-name="Address container Footer left">
            <div className="font-semibold text-[18px] leading-snug text-white sm:text-[20px]">
              <p className="break-words [overflow-wrap:anywhere]">{companyName}</p>
            </div>
            <div className="mt-8 font-normal text-[15px] leading-[26px] text-[#d0d1d0] opacity-90 sm:mt-10 sm:text-[17px] sm:leading-[27px] max-w-[613px] whitespace-pre-wrap">
              <p className="leading-[27px] mb-0">{companyAddress}</p>
              {openingHours ? (
                <>
                  <p className="leading-[27px] mb-0">&nbsp;</p>
                  <p className="leading-[27px]">{openingHours}</p>
                </>
              ) : null}
            </div>
          </div>

          <div className="min-w-0 w-full sm:w-auto sm:max-w-[240px]" data-name="Services container">
            <p className="font-semibold leading-[normal] text-[16px] text-white tracking-[0.48px] sm:text-[17px]">
              {categoryTitle}
            </p>
            <div
              className="mt-8 flex flex-col gap-4 text-[15px] font-normal leading-[normal] text-[#d0d1d0] sm:mt-10 sm:text-[17px] cursor-pointer"
              data-name="Footer links"
            >
              {categoryLinks.map((link) => (
                <p
                  key={link.label}
                  className="opacity-90 hover:text-[#ffa411] transition-colors"
                  role="button"
                  tabIndex={0}
                  onClick={() => go(link.action, link.target)}
                  onKeyDown={(e) => e.key === "Enter" && go(link.action, link.target)}
                >
                  {link.label}
                </p>
              ))}
            </div>
          </div>

          <div
            className="min-w-0 w-full sm:w-auto sm:max-w-[280px]"
            data-name="Footer contact column"
          >
            <p className="font-semibold leading-[normal] text-[16px] text-white tracking-[0.48px] sm:text-[17px]">
              {contactTitle}
            </p>
            <div
              className="mt-8 flex flex-col gap-4 text-[15px] font-normal text-[#d0d1d0] sm:mt-10 sm:text-[17px]"
              data-name="Footer contact"
            >
              <p className="break-words opacity-90 [overflow-wrap:anywhere]">
                <span>Hotline: </span>
                <span>{hotline}</span>
              </p>
              <p className="break-all opacity-90">Email: {email}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative flex w-full max-w-[1600px] shrink-0 flex-col items-center justify-center px-4 py-5 sm:px-5 md:min-h-[86px] md:py-0">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 border border-[rgba(255,255,255,0.08)] border-solid"
        />
        <div className="relative flex min-h-[36px] w-full max-w-[1120px] flex-col gap-2 text-[13px] font-normal leading-snug text-[#a1a2a2] sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:text-[15px]">
          <p className="relative shrink-0">{copyright}</p>
          <p className="relative shrink-0 cursor-pointer hover:text-[#ffa411] transition-colors">
            {policyLabel}
          </p>
        </div>
      </div>
    </footer>
  );
};

FooterDathacons.displayName = "FooterDathacons";

export default FooterDathacons;
